import { profilesTrackingService } from "../../services";
import React, { useState, useEffect, useMemo } from "react";
import styled from "styled-components";
import {
  ModalOverlay,
  ModalContainer,
  ModalHeader,
  HeaderContent,
  HeaderIcon,
  HeaderText,
  ModalTitle,
  ModalSubtitle,
  CloseButton,
  TabsContainer,
  Tab,
  ModalBody,
  FormSection,
  SectionTitle,
  FormGrid,
  FormGroup,
  Label,
  Input,
  Select,
  TextArea,
  CharCounter,
  ModalFooter,
  FooterInfo,
  SaveFeedback,
  ButtonGroup,
  Button,
} from "./styles";
import { LocationPicker } from "./LocationPicker";
import { TimePickerCustom } from "./TimePickerCustom";
import Tooltip from "../../../../components/tooltip";
import { profileAdvisorService } from "../../services/profileAdvisorService";
import { seoProfileApi, type SeoResearch } from "../../services/seo";
import { Profile } from "./types";
import { useEditProfileForm } from "./hooks/useEditProfileForm";
import { hasNewRiskyPublishedValue, SAFE_PUBLISH_WARNING } from "./safe-publish-warning";
import { ServicesTab, PostsTab, GabaritoTab, SeoTab,
  PresencaDigitalTab,
} from "./components";
import { FieldSyncBadge } from "./components/FieldSyncBadge";
import { getVisibleCategories } from "./category-options";
import {
  createKeywordDrafts,
  filterOfficialCategorySuggestions,
  getSelectedKeywordValues,
  removeKeywordDraft,
  sanitizeKeywordSuggestions,
  updateKeywordDraftSelection,
  updateKeywordDraftValue,
  type CategorySuggestion,
  type KeywordDraft,
} from "./assistant-suggestions";
import {
  buildCompleteGabarito,
  buildPublicBusinessDescription,
} from "./profile-content-suggestions";
import {
  buildAdvisorCategories,
  buildAdvisorKeywords,
  buildFormattedAddress,
} from "./assistant-context";
import {
  appendKeywords,
  readGabaritoState,
  writeGabaritoState,
} from "./components/gabarito-utils";
import {
  getServiceAreaOrigin,
} from "./service-area-suggestions";
import { fetchServiceAreaSuggestions } from "./service-area-remote";

// Componente para ícone de informação com tooltip
const InfoIcon = styled.span`
  font-size: 16px !important;
  color: #6b7280;
  cursor: help;
  transition: color 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 6px;
  vertical-align: middle;

  &:hover {
    color: #4285f4;
  }
`;

// Re-exportar tipos para compatibilidade com código externo
export type { Profile } from "./types";

interface EditProfileModalProps {
  isOpen: boolean;
  profile: Profile | null;
  onClose: () => void;
  onSave: (updatedProfile: Profile) => Promise<void>;
  hideIATab?: boolean;
}

export const EditProfileModal: React.FC<EditProfileModalProps> = ({
  isOpen,
  profile,
  onClose,
  onSave,
  hideIATab = false,
}) => {
  const [activeTab, setActiveTab] = useState("basico");
  const [loading, setLoading] = useState(false);
  const [saveFeedback, setSaveFeedback] = useState<string | null>(null);
  const [iaDescription, setIaDescription] = useState("");
  const [iaLoadingAction, setIaLoadingAction] = useState<
    "keywords" | "categories" | "briefing" | "commercial" | null
  >(null);
  const [commercialPlan, setCommercialPlan] = useState<SeoResearch | null>(
    null,
  );
  const [iaFeedback, setIaFeedback] = useState<{
    type: "success" | "error" | "info";
    message: string;
  } | null>(null);
  const [showMapPicker, setShowMapPicker] = useState(false);
  const [suggestedCategories, setSuggestedCategories] = useState<
    CategorySuggestion[]
  >([]);
  const [keywordDrafts, setKeywordDrafts] = useState<KeywordDraft[]>([]);
  const [autoFilledAreaProfileId, setAutoFilledAreaProfileId] = useState("");
  const [coverageAreaSearchQuery, setCoverageAreaSearchQuery] = useState("");
  const [coverageAreaRadiusKm, setCoverageAreaRadiusKm] = useState(15);
  const [serviceAreaSuggestions, setServiceAreaSuggestions] = useState<
    Awaited<ReturnType<typeof fetchServiceAreaSuggestions>>
  >([]);
  const [serviceAreaSuggestionsLoading, setServiceAreaSuggestionsLoading] =
    useState(false);
  const [primaryCategoriesSearchQuery, setPrimaryCategoriesSearchQuery] =
    useState("");
  const [categoriesSearchQuery, setCategoriesSearchQuery] = useState("");
  const [categoriesTouched, setCategoriesTouched] = useState(false);
  const [categoryFeedback, setCategoryFeedback] = useState("");

  // Usar novo hook para gerenciar form data e categorias
  const { 
    formData, 
    setFormData, 
    categories, 
    loadingCategories, 
    handleChange: handleFormChange 
  } = useEditProfileForm(profile, isOpen);

  const primaryCategoryValue = useMemo(
    () =>
      (formData?.categoryId &&
      categories.some((cat) => cat.gcid === formData.categoryId)
        ? formData.categoryId
        : categories.find(
            (cat) =>
              cat.category === formData?.category ||
              cat.category === formData?.categoryId,
          )?.gcid) || "",
    [categories, formData?.category, formData?.categoryId],
  );

  const visiblePrimaryCategories = useMemo(
    () =>
      getVisibleCategories(
        categories,
        primaryCategoriesSearchQuery,
        primaryCategoryValue ? [primaryCategoryValue] : [],
        [formData?.category, formData?.categoryId].filter(
          (value): value is string => Boolean(value),
        ),
      ),
    [
      categories,
      primaryCategoriesSearchQuery,
      primaryCategoryValue,
      formData?.category,
      formData?.categoryId,
    ],
  );

  const primaryCategoryOptions = useMemo(
    () =>
      visiblePrimaryCategories.map((cat) => (
        <option key={cat.gcid} value={cat.gcid}>
          {cat.category}
        </option>
      )),
    [visiblePrimaryCategories],
  );

  const visibleCategories = useMemo(
    () =>
      getVisibleCategories(
        categories,
        categoriesSearchQuery,
        formData?.secondaryCategoryIds || [],
        formData?.secondaryCategories || [],
      ),
    [
      categories,
      categoriesSearchQuery,
      formData?.secondaryCategoryIds,
      formData?.secondaryCategories,
    ],
  );

  const serviceAreaOrigin = useMemo(
    () =>
      getServiceAreaOrigin({
        city: formData?.city,
        address: formData?.address,
        latitude: formData?.latitude,
        longitude: formData?.longitude,
        state: formData?.state,
        serviceAreas: formData?.serviceAreas,
      }),
    [
      formData?.address,
      formData?.city,
      formData?.latitude,
      formData?.longitude,
      formData?.state,
      formData?.serviceAreas,
    ],
  );

  const selectedKeywordValues = useMemo(
    () => getSelectedKeywordValues(keywordDrafts),
    [keywordDrafts],
  );

  // Reset categoriesSearchQuery quando modal fecha
  useEffect(() => {
    if (!isOpen) {
      setShowMapPicker(false);
      setPrimaryCategoriesSearchQuery("");
      setCategoriesSearchQuery("");
      setCoverageAreaSearchQuery("");
      setCoverageAreaRadiusKm(15);
      setServiceAreaSuggestions([]);
      setServiceAreaSuggestionsLoading(false);
      setSuggestedCategories([]);
      setKeywordDrafts([]);
      setAutoFilledAreaProfileId("");
      setSaveFeedback(null);
      setCategoriesTouched(false);
      setCategoryFeedback("");
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !formData?.id) {
      setServiceAreaSuggestions([]);
      return;
    }

    let active = true;
    setServiceAreaSuggestionsLoading(true);

    fetchServiceAreaSuggestions(formData.id, formData, {
      query: coverageAreaSearchQuery,
      radiusKm: coverageAreaRadiusKm,
    })
      .then((suggestions) => {
        if (active) setServiceAreaSuggestions(suggestions);
      })
      .catch(() => {
        if (active) setServiceAreaSuggestions([]);
      })
      .finally(() => {
        if (active) setServiceAreaSuggestionsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [
    coverageAreaRadiusKm,
    coverageAreaSearchQuery,
    formData,
    formData?.id,
    isOpen,
  ]);

  if (!isOpen) return null;

  // Se formData ainda não foi inicializado, não renderiza nada
  if (!formData) return null;

  // Usar o handleChange do hook ou o local
  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    handleFormChange(e);
  };

  const handlePrimaryCategoryChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const selectedId = e.target.value;
    const selected = categories.find((c) => c.gcid === selectedId);
    setCategoriesTouched(true);
    setCategoryFeedback("");
    setFormData((prev) =>
      prev
        ? {
            ...prev,
            categoryId: selectedId || undefined,
            category: selected?.category || "",
            secondaryCategoryIds: (prev.secondaryCategoryIds || []).filter(
              (id) => id !== selectedId,
            ),
            secondaryCategories: (prev.secondaryCategories || []).filter(
              (name) => name !== selected?.category,
            ),
          }
        : null
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData) return;
    if (profile && hasNewRiskyPublishedValue(profile, formData) && !window.confirm(SAFE_PUBLISH_WARNING)) {
      return;
    }

    setSaveFeedback(null);
    setLoading(true);
    try {
      await onSave(
        categoriesTouched || !profile
          ? formData
          : {
              ...formData,
              category: profile.category,
              categoryId: profile.categoryId,
              secondaryCategories: profile.secondaryCategories,
              secondaryCategoryIds: profile.secondaryCategoryIds,
            },
      );
      onClose();
    } catch (error) {
      setSaveFeedback(
        getErrorMessage(error, "Nao foi possivel salvar as alteracoes do perfil."),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const getErrorMessage = (error: unknown, fallback: string) =>
    error instanceof Error ? error.message : fallback;

  const getCurrentGabarito = () =>
    formData ? readGabaritoState(formData.id) : null;

  const applySuggestedCategory = (category: CategorySuggestion) => {
    setCategoriesTouched(true);
    setCategoryFeedback("");
    setFormData((prev) => {
      if (!prev) return null;

      if (category.primary || !prev.categoryId) {
        return {
          ...prev,
          category: category.category,
          categoryId: category.gcid,
        };
      }

      return {
        ...prev,
        secondaryCategories: Array.from(
          new Set([...(prev.secondaryCategories || []), category.category]),
        ).slice(0, 8),
        secondaryCategoryIds: Array.from(
          new Set([...(prev.secondaryCategoryIds || []), category.gcid]),
        ).slice(0, 8),
      };
    });
  };

  const addServiceArea = (area: string) => {
    const nextArea = area.trim();
    if (!nextArea) return;

    setFormData((prev) =>
      prev
        ? {
            ...prev,
            serviceAreas: Array.from(
              new Map(
                [...(prev.serviceAreas || []), nextArea].map((serviceArea) => [
                  serviceArea.toLowerCase().trim(),
                  serviceArea,
                ]),
              ).values(),
            ),
          }
        : null,
    );
    setCoverageAreaSearchQuery("");
  };

  const removeServiceArea = (area: string) => {
    setFormData((prev) =>
      prev
        ? {
            ...prev,
            serviceAreas: (prev.serviceAreas || []).filter(
              (serviceArea) =>
                serviceArea.toLowerCase().trim() !== area.toLowerCase().trim(),
            ),
          }
        : null,
    );
  };

  const addTypedServiceArea = () => {
    addServiceArea(coverageAreaSearchQuery);
  };

  const fillSuggestedServiceAreas = () => {
    setFormData((prev) =>
      prev
        ? {
            ...prev,
            serviceAreas: Array.from(
              new Map(
                [
                  ...(prev.serviceAreas || []),
                  ...serviceAreaSuggestions.map((suggestion) => suggestion.label),
                ].map((serviceArea) => [
                  serviceArea.toLowerCase().trim(),
                  serviceArea,
                ]),
              ).values(),
            ),
          }
        : null,
    );
    if (formData?.id) {
      setAutoFilledAreaProfileId(formData.id);
    }
  };

  const handleSuggestCategoriesWithContext = async () => {
    setSaveFeedback(null);
    if (!formData || !iaDescription.trim()) {
      setIaFeedback({
        type: "info",
        message: "Descreva o negocio ou objetivo SEO para receber sugestoes.",
      });
      return;
    }

    setIaLoadingAction("categories");
    setIaFeedback(null);
    try {
      const suggestions = await profileAdvisorService.getSuggestedCategories({
        displayName: formData.name,
        formattedAddress: buildFormattedAddress(formData),
        types: [iaDescription],
      });
      const officialSuggestions = filterOfficialCategorySuggestions({
        suggestions,
        officialCategories: categories,
        context: [
          formData.name,
          formData.category,
          buildFormattedAddress(formData),
          iaDescription,
        ].join(" "),
      });

      setSuggestedCategories(officialSuggestions);
      if (officialSuggestions.length > 0) {
        setActiveTab("basico");
      }
      setIaFeedback({
        type: officialSuggestions.length > 0 ? "success" : "info",
        message:
          officialSuggestions.length > 0
            ? "Categorias oficiais sugeridas na aba Informacoes Basicas."
            : "Nenhuma categoria oficial foi compatível com este contexto.",
      });
    } catch (error) {
      setIaFeedback({
        type: "error",
        message: getErrorMessage(error, "Erro ao sugerir categorias."),
      });
    } finally {
      setIaLoadingAction(null);
    }
  };

  const handleGenerateCommercialPlan = async () => {
    setSaveFeedback(null);
    if (!formData || !iaDescription.trim()) {
      setIaFeedback({
        type: "info",
        message:
          "Descreva o objetivo comercial para gerar o plano de SEO.",
      });
      return;
    }
    setIaLoadingAction("commercial");
    setIaFeedback(null);
    try {
      const plan = await seoProfileApi.research(formData.id, iaDescription);
      setCommercialPlan(plan);
      const total = plan.itens.length + plan.produtos.length;
      setIaFeedback({
        type: total > 0 ? "success" : "info",
        message:
          total > 0
            ? "Plano comercial criado. Abra a aba SEO para revisar e publicar."
            : "A IA nao encontrou oportunidades comerciais suficientes para este contexto.",
      });
    } catch (error) {
      setIaFeedback({
        type: "error",
        message: getErrorMessage(error, "Erro ao gerar plano comercial."),
      });
    } finally {
      setIaLoadingAction(null);
    }
  };

  const handleSuggestKeywordsWithContext = async () => {
    setSaveFeedback(null);
    if (!formData || !iaDescription.trim()) {
      setIaFeedback({
        type: "info",
        message: "Descreva o objetivo SEO para receber palavras-chave.",
      });
      return;
    }

    setIaLoadingAction("keywords");
    setIaFeedback(null);
    try {
      const gabarito = getCurrentGabarito();
      const keywords = await profileAdvisorService.getSuggestedKeywords({
        displayName: formData.name,
        formattedAddress: buildFormattedAddress(formData),
        categories: buildAdvisorCategories(formData, iaDescription),
        currentKeywords: [
          ...(gabarito?.postKeywords || "").split(","),
          ...(gabarito?.commentKeywords || "").split(","),
        ]
          .map((k) => k.trim())
          .filter(Boolean),
      });
      const contextualKeywords = buildAdvisorKeywords({
        suggestedKeywords: keywords,
        iaDescription,
        gabaritoPostKeywords: gabarito?.postKeywords || "",
        gabaritoCommentKeywords: gabarito?.commentKeywords || "",
      });

      setKeywordDrafts(createKeywordDrafts(contextualKeywords, formData));
      setIaFeedback({
        type: contextualKeywords.length > 0 ? "success" : "info",
        message:
          contextualKeywords.length > 0
            ? "Revise as palavras-chave antes de aplicar no Gabarito."
            : "A IA nao retornou palavras-chave para este contexto.",
      });
    } catch (error) {
      setIaFeedback({
        type: "error",
        message: getErrorMessage(error, "Erro ao sugerir palavras-chave."),
      });
    } finally {
      setIaLoadingAction(null);
    }
  };

  const handleGenerateBriefingWithContext = async () => {
    setSaveFeedback(null);
    if (!formData || !iaDescription.trim()) {
      setIaFeedback({
        type: "info",
        message: "Descreva o negocio ou objetivo SEO para gerar a descricao.",
      });
      return;
    }

    setIaLoadingAction("briefing");
    setIaFeedback(null);
    try {
      const gabarito = getCurrentGabarito();
      const categoriesArray = buildAdvisorCategories(
        formData,
        iaDescription,
        suggestedCategories.map((category) => category.category),
      );
      const keywordsArray = buildAdvisorKeywords({
        suggestedKeywords: sanitizeKeywordSuggestions(
          selectedKeywordValues,
          formData,
        ),
        iaDescription,
        gabaritoPostKeywords: gabarito?.postKeywords || "",
        gabaritoCommentKeywords: gabarito?.commentKeywords || "",
      });

      await profileAdvisorService.getSuggestedBriefing({
        displayName: formData.name,
        formattedAddress: buildFormattedAddress(formData),
        categories: categoriesArray,
        keywords: keywordsArray.length > 0 ? keywordsArray : undefined,
      });

      const publicDescription = buildPublicBusinessDescription({
        profile: formData,
        keywords: keywordsArray,
      });
      setFormData((prev) =>
        prev ? { ...prev, description: publicDescription } : null,
      );
      setActiveTab("descricao");
      setIaFeedback({
        type: "success",
        message: "Descricao publica preenchida na aba Descricao.",
      });
    } catch (error) {
      setIaFeedback({
        type: "error",
        message: `Não foi possível gerar descrição pela IA: ${getErrorMessage(
          error,
          "motivo não identificado.",
        )}`,
      });
    } finally {
      setIaLoadingAction(null);
    }
  };

  const handleApplyKeywordsToGabarito = async () => {
    if (!formData || selectedKeywordValues.length === 0) return;
    const cleanKeywords = sanitizeKeywordSuggestions(
      selectedKeywordValues,
      formData,
    );
    if (cleanKeywords.length === 0) {
      setIaFeedback({
        type: "info",
        message: "Revise as palavras-chave antes de aplicar no Gabarito.",
      });
      return;
    }
    const gabarito = readGabaritoState(formData.id);
    const updatedGabarito = buildCompleteGabarito({
      profile: formData,
      existing: {
        ...gabarito,
        postKeywords: appendKeywords(gabarito.postKeywords, cleanKeywords),
      },
      keywords: cleanKeywords,
    });

    writeGabaritoState(formData.id, updatedGabarito);
    try {
      await profilesTrackingService.saveGabaritoText(formData.id, {
        postKeywords: updatedGabarito.postKeywords ?? "",
        commentKeywords: updatedGabarito.commentKeywords ?? "",
        postNotes: updatedGabarito.postNotes ?? "",
        commentNotes: updatedGabarito.commentNotes ?? "",
      });
      setIaFeedback({
        type: "success",
        message: "Palavras-chave revisadas aplicadas no Gabarito deste perfil.",
      });
    } catch (error: unknown) {
      setIaFeedback({
        type: "error",
        message: `Erro ao salvar Gabarito: ${getErrorMessage(
          error,
          "motivo nao identificado.",
        )}`,
      });
    }
  };

  const tabs = [
    { id: "basico", label: "Informações Básicas", icon: "business" },
    { id: "contato", label: "Contato", icon: "contact_phone" },
    { id: "local", label: "Local e Áreas", icon: "place" },
    { id: "horarios", label: "Horários", icon: "schedule" },
    { id: "descricao", label: "Descrição", icon: "description" },
    { id: "servicos", label: "Serviços", icon: "build" },
    { id: "postagens", label: "Postagens", icon: "feed" },
    { id: "gabarito", label: "Gabarito", icon: "assignment" },
    { id: "atributos", label: "Mais Atributos", icon: "tune" },
    { id: "ia", label: "Assistente IA", icon: "auto_awesome" },
    { id: "seo", label: "SEO", icon: "travel_explore" },
    { id: "presenca", label: "Presença Digital", icon: "qr_code_2" },
  ].filter((tab) => !(hideIATab && tab.id === "ia"));

  return (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContainer>
        <ModalHeader>
          <HeaderContent>
            <HeaderIcon>
              <span className="material-icons">store</span>
            </HeaderIcon>
            <HeaderText>
              <ModalTitle>Gerenciar Perfil</ModalTitle>
              <ModalSubtitle>{formData.name}</ModalSubtitle>
            </HeaderText>
          </HeaderContent>
          <CloseButton onClick={onClose}>
            <span className="material-icons">close</span>
          </CloseButton>
        </ModalHeader>

        <TabsContainer>
          {tabs.map((tab) => (
            <Tab
              key={tab.id}
              $active={activeTab === tab.id}
              onClick={() => {
                if (tab.id !== "local") {
                  setShowMapPicker(false);
                }
                setActiveTab(tab.id);
                setSaveFeedback(null);
              }}
            >
              <span className="material-icons">{tab.icon}</span>
              {tab.label}
            </Tab>
          ))}
        </TabsContainer>

        <form
          onSubmit={handleSubmit}
          style={{
            display: "flex",
            flexDirection: "column",
            flex: "1 1 auto",
            minHeight: 0,
          }}
        >
          <ModalBody>
            {activeTab === "basico" && (
              <FormSection>
                <SectionTitle>Informações Básicas do Negócio</SectionTitle>

                <FormGrid>
                  <FormGroup>
                    <Label htmlFor="name">
                      <span className="material-icons">business</span>
                      Nome da Empresa
                      <FieldSyncBadge kind="backend" />
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Ex: Restaurante Sabor da Terra"
                      required
                    />
                  </FormGroup>
                </FormGrid>

                <FormGrid>
                  <FormGroup>
                    <Label htmlFor="category">
                      <span className="material-icons">category</span>
                      Categoria Principal
                      <FieldSyncBadge kind="backend" />
                    </Label>
                    <Select
                      id="category"
                      name="category"
                      value={primaryCategoryValue}
                      onChange={handlePrimaryCategoryChange}
                    >
                      <option value="">Selecione uma categoria</option>
                      {primaryCategoryOptions}
                    </Select>
                    <div style={{ marginTop: "8px", color: "#475569", fontSize: "12px" }}>
                      Atual no Google: <strong>{formData.category || "Nao informada"}</strong>
                      {formData.categoryId ? ` (${formData.categoryId})` : ""}
                    </div>
                    <Input
                      type="text"
                      placeholder="Pesquisar categoria principal..."
                      value={primaryCategoriesSearchQuery}
                      onChange={(e) =>
                        setPrimaryCategoriesSearchQuery(e.target.value)
                      }
                      style={{
                        marginTop: "8px",
                        padding: "10px 12px",
                        fontSize: "14px",
                      }}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="status">
                      <span className="material-icons">toggle_on</span>
                      <FieldSyncBadge kind="backend" />
                      Status do Negócio
                    </Label>
                    <Select
                      id="status"
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                    >
                      <option value="active">✓ Aberto e Operacional</option>
                      <option value="paused">⏸ Fechado Temporariamente</option>
                      <option value="pending">🕐 Abrirá em Breve</option>
                      <option value="preparing">⚙ Em Preparação</option>
                    </Select>
                  </FormGroup>
                </FormGrid>

                {suggestedCategories.length > 0 && (
                  <div
                    style={{
                      marginBottom: "18px",
                      padding: "14px",
                      background: "#f0f9ff",
                      border: "1px solid #bae6fd",
                      borderRadius: "10px",
                    }}
                  >
                    <strong
                      style={{
                        display: "block",
                        color: "#0c4a6e",
                        marginBottom: "6px",
                      }}
                    >
                      Sugestões oficiais de categoria
                    </strong>
                    <span
                      style={{
                        display: "block",
                        color: "#475569",
                        fontSize: "13px",
                        marginBottom: "12px",
                      }}
                    >
                      Escolha somente categorias da lista oficial do Google para
                      evitar sugestões fora do segmento.
                    </span>
                    <div
                      style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}
                    >
                      {suggestedCategories.map((category) => (
                        <button
                          key={category.gcid}
                          type="button"
                          onClick={() => applySuggestedCategory(category)}
                          style={{
                            border: "1px solid #bfdbfe",
                            borderRadius: "999px",
                            background: category.primary ? "#1a73e8" : "#e8f0fe",
                            color: category.primary ? "#ffffff" : "#1967d2",
                            cursor: "pointer",
                            fontSize: "13px",
                            fontWeight: 600,
                            padding: "8px 12px",
                          }}
                        >
                          {category.primary ? "Principal: " : "Adicionar: "}
                          {category.category}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <FormGroup>
                  <Label htmlFor="openingDate">
                    <span className="material-icons">event</span>
                    <FieldSyncBadge kind="backend" />
                    Data de Abertura
                    <Tooltip
                      title="Data em que o negócio foi inaugurado ou iniciou suas atividades. Ajuda a mostrar há quanto tempo você está no mercado"
                      arrow
                      placement="top"
                    >
                      <InfoIcon className="material-icons">info</InfoIcon>
                    </Tooltip>
                  </Label>
                  <Input
                    id="openingDate"
                    name="openingDate"
                    type="date"
                    value={formData.openingDate || ""}
                    onChange={handleChange}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>
                    <span className="material-icons">list</span>
                    Categorias Adicionais (opcional)
                    <FieldSyncBadge kind="backend" />
                    <Tooltip
                      title="Selecione outras categorias que descrevem seu negócio além da principal. Ex: 'Pizzaria' pode ter 'Delivery de Comida', 'Restaurante Italiano'"
                      arrow
                      placement="top"
                    >
                      <InfoIcon className="material-icons">info</InfoIcon>
                    </Tooltip>
                  </Label>
                  <div style={{ marginBottom: "10px", color: "#475569", fontSize: "12px" }}>
                    Fonte: sincronizacao do Google. Selecione no maximo 8 categorias
                    secundarias. A categoria principal nao pode ser repetida.
                  </div>
                  <div style={{ marginBottom: "10px", color: "#b45309", fontSize: "12px", fontWeight: 600 }}>
                    Atencao: ao remover uma categoria, o Google pode excluir os servicos
                    ligados a ela e pode nao permitir recria-los automaticamente.
                  </div>
                  {categoryFeedback && (
                    <div style={{ marginBottom: "10px", color: "#b45309", fontSize: "13px", fontWeight: 600 }}>
                      {categoryFeedback}
                    </div>
                  )}

                  {loadingCategories ? (
                    <div
                      style={{
                        padding: "20px",
                        textAlign: "center",
                        color: "#6b7280",
                        fontSize: "14px",
                      }}
                    >
                      Carregando categorias...
                    </div>
                  ) : (
                    <>
                      <Input
                        type="text"
                        placeholder="🔍 Pesquisar categorias..."
                        value={categoriesSearchQuery}
                        onChange={(e) =>
                          setCategoriesSearchQuery(e.target.value)
                        }
                        style={{
                          marginBottom: "12px",
                          padding: "10px 12px",
                          fontSize: "14px",
                        }}
                      />
                      <div
                        style={{
                          maxHeight: "300px",
                          overflowY: "auto",
                          border: "1px solid #e5e7eb",
                          borderRadius: "8px",
                          padding: "12px",
                          background: "#f9fafb",
                        }}
                      >
                        {categories.length === 0 ? (
                          <div
                            style={{
                              padding: "20px",
                              textAlign: "center",
                              color: "#6b7280",
                              fontSize: "14px",
                            }}
                          >
                            Nenhuma categoria disponível
                          </div>
                        ) : (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "8px",
                            }}
                          >
                            {visibleCategories
                              .map((cat) => (
                                <label
                                  key={cat.gcid}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "10px",
                                    padding: "8px 12px",
                                    background: "#ffffff",
                                    borderRadius: "6px",
                                    cursor: "pointer",
                                    transition: "all 0.2s",
                                    border: "1px solid transparent",
                                  }}
                                  onMouseOver={(e) => {
                                    e.currentTarget.style.borderColor =
                                      "#4285f4";
                                    e.currentTarget.style.background =
                                      "#f0f9ff";
                                  }}
                                  onMouseOut={(e) => {
                                    e.currentTarget.style.borderColor =
                                      "transparent";
                                    e.currentTarget.style.background =
                                      "#ffffff";
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={
                                      formData.secondaryCategoryIds?.includes(
                                        cat.gcid
                                      ) ||
                                      formData.secondaryCategories?.includes(
                                        cat.category
                                      ) ||
                                      false
                                    }
                                    onChange={(e) => {
                                      const currentNames =
                                        formData.secondaryCategories || [];
                                      const currentIds =
                                        formData.secondaryCategoryIds || [];

                                      if (
                                        e.target.checked &&
                                        (cat.gcid === primaryCategoryValue ||
                                          cat.category === formData.category)
                                      ) {
                                        setCategoryFeedback(
                                          "A categoria principal nao pode ser repetida como secundaria.",
                                        );
                                        return;
                                      }
                                      if (
                                        e.target.checked &&
                                        currentIds.length >= 8 &&
                                        !currentIds.includes(cat.gcid)
                                      ) {
                                        setCategoryFeedback(
                                          "Limite atingido: escolha no maximo 8 categorias secundarias.",
                                        );
                                        return;
                                      }
                                      setCategoriesTouched(true);
                                      setCategoryFeedback("");

                                      const updatedNames = e.target.checked
                                        ? Array.from(
                                            new Set([...currentNames, cat.category])
                                          )
                                        : currentNames.filter(
                                            (c) => c !== cat.category
                                          );

                                      const updatedIds = e.target.checked
                                        ? Array.from(
                                            new Set([...currentIds, cat.gcid])
                                          )
                                        : currentIds.filter(
                                            (id) => id !== cat.gcid
                                          );
                                      setFormData((prev) =>
                                        prev
                                          ? {
                                              ...prev,
                                              secondaryCategories: updatedNames,
                                              secondaryCategoryIds: updatedIds,
                                            }
                                          : null
                                      );
                                    }}
                                    disabled={
                                      cat.gcid === primaryCategoryValue ||
                                      cat.category === formData.category ||
                                      ((formData.secondaryCategoryIds || []).length >= 8 &&
                                        !(formData.secondaryCategoryIds || []).includes(cat.gcid))
                                    }
                                    style={{
                                      width: "18px",
                                      height: "18px",
                                      cursor: "pointer",
                                      accentColor: "#4285f4",
                                    }}
                                  />
                                  <span
                                    style={{
                                      fontSize: "14px",
                                      color: "#374151",
                                      flex: 1,
                                    }}
                                  >
                                    {cat.category}
                                  </span>
                                </label>
                              ))}
                            {visibleCategories.length === 0 && (
                              <div
                                style={{
                                  padding: "20px",
                                  textAlign: "center",
                                  color: "#6b7280",
                                  fontSize: "14px",
                                }}
                              >
                                Nenhuma categoria encontrada
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  {formData.secondaryCategories &&
                    formData.secondaryCategories.length > 0 && (
                      <div
                        style={{
                          marginTop: "12px",
                          padding: "12px",
                          background: "#f0f9ff",
                          borderRadius: "6px",
                          border: "1px solid #bae6fd",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "12px",
                            fontWeight: 600,
                            color: "#0c4a6e",
                            marginBottom: "8px",
                          }}
                        >
                          Categorias Selecionadas (
                          {(formData.secondaryCategories || []).length}):
                        </div>
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "6px",
                          }}
                        >
                          {(formData.secondaryCategories || []).map((cat) => (
                            <span
                              key={cat}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "6px",
                                padding: "4px 10px",
                                background: "#4285f4",
                                color: "#ffffff",
                                borderRadius: "16px",
                                fontSize: "12px",
                                fontWeight: 500,
                              }}
                            >
                              {cat}
                              <button
                                type="button"
                                onClick={() => {
                                  setCategoriesTouched(true);
                                  setCategoryFeedback("");
                                  const categoryId = categories.find(
                                    (category) => category.category === cat
                                  )?.gcid;
                                  const updated =
                                    formData.secondaryCategories?.filter(
                                      (c) => c !== cat
                                    ) || [];
                                  setFormData((prev) =>
                                    prev
                                      ? {
                                          ...prev,
                                          secondaryCategories: updated,
                                          secondaryCategoryIds:
                                            prev.secondaryCategoryIds?.filter(
                                              (id) => id !== categoryId
                                            ),
                                        }
                                      : null
                                  );
                                }}
                                style={{
                                  background: "transparent",
                                  border: "none",
                                  color: "#ffffff",
                                  cursor: "pointer",
                                  padding: "0",
                                  display: "flex",
                                  alignItems: "center",
                                  fontSize: "16px",
                                }}
                              >
                                <span
                                  className="material-icons"
                                  style={{ fontSize: "16px" }}
                                >
                                  close
                                </span>
                              </button>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                </FormGroup>
              </FormSection>
            )}

            {activeTab === "contato" && (
              <FormSection>
                <SectionTitle>Informações de Contato</SectionTitle>

                <FormGrid>
                  <FormGroup>
                    <Label htmlFor="phone">
                      <span className="material-icons">phone</span>
                      Telefone Principal
                      <FieldSyncBadge kind="backend" />
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone || ""}
                      onChange={handleChange}
                      placeholder="(11) 98765-4321"
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="email">
                      <span className="material-icons">email</span>
                      E-mail de Contato
                      <FieldSyncBadge kind="backend" />
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email || ""}
                      onChange={handleChange}
                      placeholder="contato@suaempresa.com.br"
                    />
                  </FormGroup>
                </FormGrid>

                <FormGroup>
                  <Label htmlFor="website">
                    <span className="material-icons">language</span>
                    <FieldSyncBadge kind="backend" />
                    Website / Página Oficial
                  </Label>
                  <Input
                    id="website"
                    name="website"
                    type="url"
                    value={formData.website || ""}
                    onChange={handleChange}
                    placeholder="https://www.suaempresa.com.br"
                  />
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="whatsapp">
                    <span className="material-icons">chat</span>
                    WhatsApp (Chat)
                    <FieldSyncBadge kind="backend" />
                    <Tooltip
                      title="Link direto para iniciar uma conversa no WhatsApp. Use o formato: https://wa.me/5511999999999 (código do país + DDD + número)"
                      arrow
                      placement="top"
                    >
                      <InfoIcon className="material-icons">info</InfoIcon>
                    </Tooltip>
                  </Label>
                  <Input
                    id="whatsapp"
                    name="whatsapp"
                    type="text"
                    value={formData.whatsapp || ""}
                    onChange={handleChange}
                    placeholder="https://wa.me/5511999999999"
                  />
                </FormGroup>

                <SectionTitle style={{ marginTop: "24px" }}>
                  Perfis de Redes Sociais
                </SectionTitle>

                <FormGroup>
                  <Label htmlFor="socialMedia.facebook">
                    <span className="material-icons">facebook</span>
                    Facebook
                    <FieldSyncBadge kind="backend" />
                    <Tooltip
                      title="Link da página oficial do seu negócio no Facebook. Ajuda clientes a encontrarem suas publicações e avaliações"
                      arrow
                      placement="top"
                    >
                      <InfoIcon className="material-icons">info</InfoIcon>
                    </Tooltip>
                  </Label>
                  <Input
                    id="socialMedia.facebook"
                    name="socialMedia.facebook"
                    type="url"
                    value={formData.socialMedia?.facebook || ""}
                    onChange={handleChange}
                    placeholder="https://www.facebook.com/suaempresa"
                  />
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="socialMedia.instagram">
                    <span className="material-icons">camera_alt</span>
                    Instagram
                    <FieldSyncBadge kind="backend" />
                    <Tooltip
                      title="Perfil comercial do Instagram. Importante para negócios visuais como restaurantes, lojas e serviços"
                      arrow
                      placement="top"
                    >
                      <InfoIcon className="material-icons">info</InfoIcon>
                    </Tooltip>
                  </Label>
                  <Input
                    id="socialMedia.instagram"
                    name="socialMedia.instagram"
                    type="url"
                    value={formData.socialMedia?.instagram || ""}
                    onChange={handleChange}
                    placeholder="https://www.instagram.com/suaempresa"
                  />
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="socialMedia.linkedin">
                    <span className="material-icons">work</span>
                    LinkedIn
                    <FieldSyncBadge kind="backend" />
                  </Label>
                  <Input
                    id="socialMedia.linkedin"
                    name="socialMedia.linkedin"
                    type="url"
                    value={formData.socialMedia?.linkedin || ""}
                    onChange={handleChange}
                    placeholder="https://www.linkedin.com/company/suaempresa"
                  />
                </FormGroup>
              </FormSection>
            )}

            {activeTab === "local" && (
              <FormSection>
                <SectionTitle>Local da Empresa</SectionTitle>
                <div
                  role="status"
                  style={{
                    marginBottom: "20px",
                    padding: "12px 14px",
                    background: "#fef3c7",
                    border: "1px solid #fcd34d",
                    borderRadius: "8px",
                    color: "#92400e",
                    fontSize: "13px",
                  }}
                >
                  Atenção: alterar o endereço atualiza a ficha da empresa no
                  Google e pode exigir reverificação, limitando a exibição por
                  alguns dias. Edite apenas se realmente necessário.
                </div>

                <FormGroup>
                  <Label htmlFor="address">
                    <span className="material-icons">home</span>
                    <FieldSyncBadge kind="backend" />
                    Endereço Completo
                  </Label>
                  <Input
                    id="address"
                    name="address"
                    type="text"
                    value={formData.address || ""}
                    onChange={handleChange}
                    placeholder="Rua, Avenida, número, complemento"
                  />
                </FormGroup>

                <FormGrid>
                  <FormGroup>
                    <Label htmlFor="city">
                      <span className="material-icons">location_city</span>
                      <FieldSyncBadge kind="backend" />
                      Cidade
                    </Label>
                    <Input
                      id="city"
                      name="city"
                      type="text"
                    value={formData.city || ""}
                      onChange={handleChange}
                      placeholder="Ex: São Paulo"
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="state">
                      <span className="material-icons">map</span>
                      <FieldSyncBadge kind="backend" />
                      Estado (UF)
                    </Label>
                    <Input
                      id="state"
                      name="state"
                      type="text"
                    value={formData.state || ""}
                      onChange={handleChange}
                      placeholder="Ex: SP"
                      maxLength={2}
                    />
                  </FormGroup>
                </FormGrid>

                <FormGrid>
                  <FormGroup>
                    <Label htmlFor="zipCode">
                      <span className="material-icons">local_post_office</span>
                      <FieldSyncBadge kind="backend" />
                      CEP
                    </Label>
                    <Input
                      id="zipCode"
                      name="zipCode"
                      type="text"
                    value={formData.zipCode || ""}
                      onChange={handleChange}
                      placeholder="00000-000"
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="country">
                      <span className="material-icons">public</span>
                      <FieldSyncBadge kind="backend" />
                      País
                    </Label>
                    <Input
                      id="country"
                      name="country"
                      type="text"
                    value={formData.country || ""}
                      onChange={handleChange}
                      placeholder="Brasil"
                    />
                  </FormGroup>
                </FormGrid>

                <FormGroup style={{ marginTop: "24px" }}>
                  <Label>
                    <span className="material-icons">place</span>
                    <FieldSyncBadge kind="backend" />
                    Localização no Mapa
                    <Tooltip
                      title="Defina a localização exata do seu negócio. Clientes podem usar isso para chegar até você e aparecer em buscas 'perto de mim'"
                      arrow
                      placement="top"
                    >
                      <InfoIcon className="material-icons">info</InfoIcon>
                    </Tooltip>
                  </Label>
                  {showMapPicker ? (
                    <LocationPicker
                      latitude={formData.latitude ?? serviceAreaOrigin?.lat}
                      longitude={formData.longitude ?? serviceAreaOrigin?.lng}
                      onLocationChange={(lat, lng) => {
                        setFormData((prev) =>
                          prev
                            ? { ...prev, latitude: lat, longitude: lng }
                            : null
                          );
                      }}
                      onRadiusChange={setCoverageAreaRadiusKm}
                      radiusKm={coverageAreaRadiusKm}
                      startAddress={[
                        formData.address,
                        [formData.city, formData.state]
                          .filter(Boolean)
                          .join(" - "),
                      ]
                        .filter(Boolean)
                        .join(", ")}
                    />
                  ) : (
                    <div
                      style={{
                        padding: "14px 16px",
                        background: "#f8fafc",
                        border: "1px solid #e5e7eb",
                        borderRadius: "8px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "12px",
                      }}
                    >
                      <span style={{ color: "#5f6368", fontSize: "13px" }}>
                        {formData.latitude && formData.longitude
                          ? `Coordenadas: ${formData.latitude.toFixed(6)}, ${formData.longitude.toFixed(6)}`
                          : "Sem coordenadas definidas"}
                      </span>
                      <Button
                        type="button"
                        $secondary
                        onClick={() => setShowMapPicker(true)}
                        style={{ padding: "10px 16px" }}
                      >
                        <span className="material-icons">map</span>
                        <span>Abrir mapa</span>
                      </Button>
                    </div>
                  )}
                </FormGroup>

                <SectionTitle style={{ marginTop: "32px" }}>
                  Área de Cobertura
                </SectionTitle>

                <FormGroup>
                  <Label>
                    <span className="material-icons">explore</span>
                    <FieldSyncBadge kind="backend" />
                    Areas atendidas
                    <Tooltip
                      title="Especifique bairros, cidades ou regiões onde você oferece serviços ou entregas. Útil para negócios com delivery ou prestação de serviços"
                      arrow
                      placement="top"
                    >
                      <InfoIcon className="material-icons">info</InfoIcon>
                    </Tooltip>
                  </Label>
                  <div
                    style={{
                      background: "#fbfdff",
                      border: "1px solid #dbeafe",
                      borderRadius: "12px",
                      padding: "16px",
                    }}
                  >
                    <div
                      style={{
                        alignItems: "center",
                        display: "grid",
                        gap: "12px",
                        gridTemplateColumns:
                          "repeat(auto-fit, minmax(180px, 1fr))",
                      }}
                    >
                      <Input
                        aria-label="Area de pesquisa"
                        value={coverageAreaSearchQuery}
                        onChange={(e) =>
                          setCoverageAreaSearchQuery(e.target.value)
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            addTypedServiceArea();
                          }
                        }}
                        placeholder="Area de pesquisa"
                      />
                      <Select
                        aria-label="Raio de cobertura"
                        value={coverageAreaRadiusKm}
                        onChange={(e) =>
                          setCoverageAreaRadiusKm(Number(e.target.value))
                        }
                      >
                        <option value={2}>Raio 2 km</option>
                        <option value={5}>Raio 5 km</option>
                        <option value={10}>Raio 10 km</option>
                        <option value={15}>Raio 15 km</option>
                      </Select>
                      <Button
                        type="button"
                        $secondary
                        onClick={addTypedServiceArea}
                        disabled={!coverageAreaSearchQuery.trim()}
                        style={{ padding: "12px 14px" }}
                      >
                        <span className="material-icons">add</span>
                        <span>Adicionar</span>
                      </Button>
                    </div>

                    <div style={{ marginTop: "18px" }}>
                      <strong
                        style={{
                          color: "#5f6368",
                          display: "block",
                          fontSize: "13px",
                          fontWeight: 600,
                          marginBottom: "10px",
                        }}
                      >
                        Areas selecionadas
                      </strong>
                      {(formData.serviceAreas || []).length > 0 ? (
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "8px",
                          }}
                        >
                          {(formData.serviceAreas || []).map((area) => (
                            <button
                              key={area}
                              type="button"
                              onClick={() => removeServiceArea(area)}
                              style={{
                                alignItems: "center",
                                background: "#e8eaed",
                                border: "1px solid #d1d5db",
                                borderRadius: "999px",
                                color: "#5f6368",
                                cursor: "pointer",
                                display: "inline-flex",
                                fontSize: "13px",
                                fontWeight: 600,
                                gap: "8px",
                                padding: "8px 12px",
                              }}
                            >
                              <span>{area}</span>
                              <span
                                className="material-icons"
                                style={{ fontSize: "16px" }}
                              >
                                close
                              </span>
                            </button>
                          ))}
                        </div>
                      ) : (
                        <span style={{ color: "#9aa0a6", fontSize: "13px" }}>
                          Nenhuma area selecionada
                        </span>
                      )}
                    </div>
                  </div>
                  {autoFilledAreaProfileId === formData.id &&
                    (formData.serviceAreas || []).length > 0 && (
                      <div
                        style={{
                          marginTop: "10px",
                          padding: "10px 12px",
                          background: "#ecfdf5",
                          border: "1px solid #a7f3d0",
                          borderRadius: "8px",
                          color: "#065f46",
                          fontSize: "13px",
                          fontWeight: 600,
                        }}
                      >
                        Áreas próximas foram pré-preenchidas pela região do
                        perfil. Revise antes de salvar.
                      </div>
                    )}
                  {(serviceAreaSuggestionsLoading ||
                    serviceAreaSuggestions.length > 0) && (
                    <div
                      style={{
                        marginTop: "12px",
                        padding: "12px",
                        background: "#f8fafc",
                        border: "1px solid #e2e8f0",
                        borderRadius: "10px",
                      }}
                    >
                      <div
                        style={{
                          alignItems: "center",
                          display: "flex",
                          gap: "12px",
                          justifyContent: "space-between",
                          marginBottom: "10px",
                        }}
                      >
                        <strong style={{ color: "#334155", fontSize: "13px" }}>
                          Sugestoes de bairros e regioes proximas
                        </strong>
                        <Button
                          type="button"
                          $secondary
                          disabled={
                            serviceAreaSuggestionsLoading ||
                            serviceAreaSuggestions.length === 0
                          }
                          onClick={fillSuggestedServiceAreas}
                          style={{ padding: "8px 12px" }}
                        >
                          <span className="material-icons">add_location_alt</span>
                          <span>Preencher areas</span>
                        </Button>
                      </div>
                      {serviceAreaSuggestionsLoading ? (
                        <span style={{ color: "#64748b", fontSize: "13px" }}>
                          Buscando bairros dentro do raio...
                        </span>
                      ) : (
                        <div
                          style={{
                            display: "grid",
                            gap: "8px",
                            gridTemplateColumns:
                              "repeat(auto-fit, minmax(220px, 1fr))",
                          }}
                        >
                          {serviceAreaSuggestions.map((suggestion) => (
                            <button
                              key={suggestion.label}
                              type="button"
                              onClick={() => addServiceArea(suggestion.label)}
                              style={{
                                alignItems: "center",
                                background: "#ffffff",
                                border: "1px solid #bfdbfe",
                                borderRadius: "8px",
                                color: "#174ea6",
                                cursor: "pointer",
                                display: "flex",
                                fontSize: "13px",
                                fontWeight: 600,
                                justifyContent: "space-between",
                                minHeight: "44px",
                                padding: "10px 12px",
                                textAlign: "left",
                              }}
                            >
                              <span>{suggestion.label}</span>
                              <span
                                style={{
                                  background: "#e8f0fe",
                                  borderRadius: "999px",
                                  color: "#1967d2",
                                  flexShrink: 0,
                                  fontSize: "12px",
                                  marginLeft: "12px",
                                  padding: "4px 8px",
                                }}
                              >
                                {suggestion.distanceKm.toLocaleString("pt-BR", {
                                  maximumFractionDigits: 1,
                                  minimumFractionDigits: 0,
                                })}{" "}
                                km
                              </span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </FormGroup>

                <div
                  style={{
                    marginTop: "16px",
                    padding: "16px",
                    background: "#f0f9ff",
                    border: "1px solid #bae6fd",
                    borderRadius: "8px",
                    display: "flex",
                    gap: "12px",
                    alignItems: "flex-start",
                  }}
                >
                  <span
                    className="material-icons"
                    style={{ color: "#0284c7", fontSize: "20px" }}
                  >
                    info
                  </span>
                  <div style={{ flex: 1 }}>
                    <strong
                      style={{
                        color: "#075985",
                        display: "block",
                        marginBottom: "4px",
                      }}
                    >
                      Dica:
                    </strong>
                    <span
                      style={{
                        color: "#0c4a6e",
                        fontSize: "14px",
                        lineHeight: "1.5",
                      }}
                    >
                      Liste todas as regiões onde sua empresa oferece serviços
                      ou entrega produtos. Isso ajuda os clientes a encontrarem
                      você nas buscas locais.
                    </span>
                  </div>
                </div>
              </FormSection>
            )}

            {activeTab === "horarios" && (
              <FormSection>
                <SectionTitle>
                  Horário de Funcionamento
                  <FieldSyncBadge kind="backend" />
                </SectionTitle>

                {[
                  { day: "monday", label: "Segunda-feira" },
                  { day: "tuesday", label: "Terça-feira" },
                  { day: "wednesday", label: "Quarta-feira" },
                  { day: "thursday", label: "Quinta-feira" },
                  { day: "friday", label: "Sexta-feira" },
                  { day: "saturday", label: "Sábado" },
                  { day: "sunday", label: "Domingo" },
                ].map(({ day, label }) => {
                  const dayKey = day as
                    | "monday"
                    | "tuesday"
                    | "wednesday"
                    | "thursday"
                    | "friday"
                    | "saturday"
                    | "sunday";
                  const currentHour = formData?.hours?.[dayKey];

                  // Converter para array se necessário
                  let hoursArray: string[] = [];
                  if (typeof currentHour === "string") {
                    hoursArray =
                      currentHour.toLowerCase() === "fechado" ||
                      currentHour === ""
                        ? []
                        : [currentHour];
                  } else if (Array.isArray(currentHour)) {
                    hoursArray = (currentHour as string[]).filter(
                      (h: string) => h.toLowerCase() !== "fechado" && h !== ""
                    );
                  }

                  const isClosed = hoursArray.length === 0;

                  const addTimeSlot = () => {
                    const newSlot = "08:00 - 18:00";
                    setFormData((prev) => {
                      if (!prev) return null;
                      const currentHours = prev.hours?.[dayKey];
                      let updatedHours: string[];

                      if (typeof currentHours === "string") {
                        updatedHours =
                          currentHours.toLowerCase() === "fechado" ||
                          currentHours === ""
                            ? [newSlot]
                            : [currentHours, newSlot];
                      } else if (Array.isArray(currentHours)) {
                        updatedHours = [...currentHours, newSlot];
                      } else {
                        updatedHours = [newSlot];
                      }

                      return {
                        ...prev,
                        hours: {
                          ...prev.hours,
                          [day]: updatedHours,
                        },
                      };
                    });
                  };

                  const removeTimeSlot = (index: number) => {
                    setFormData((prev) => {
                      if (!prev) return null;
                      const currentHours = prev.hours?.[dayKey];
                      let updatedHours: string | string[];

                      if (Array.isArray(currentHours)) {
                        const filtered = currentHours.filter(
                          (_, i) => i !== index
                        );
                        updatedHours =
                          filtered.length === 0
                            ? "Fechado"
                            : filtered.length === 1
                            ? filtered[0]
                            : filtered;
                      } else {
                        updatedHours = "Fechado";
                      }

                      return {
                        ...prev,
                        hours: {
                          ...prev.hours,
                          [day]: updatedHours,
                        },
                      };
                    });
                  };

                  const updateTimeSlot = (
                    index: number,
                    openTime: string,
                    closeTime: string
                  ) => {
                    const newValue = `${openTime} - ${closeTime}`;
                    setFormData((prev) => {
                      if (!prev) return null;
                      const currentHours = prev.hours?.[dayKey];
                      let updatedHours: string | string[];

                      if (Array.isArray(currentHours)) {
                        const updated = [...currentHours];
                        updated[index] = newValue;
                        updatedHours =
                          updated.length === 1 ? updated[0] : updated;
                      } else {
                        updatedHours = newValue;
                      }

                      return {
                        ...prev,
                        hours: {
                          ...prev.hours,
                          [day]: updatedHours,
                        },
                      };
                    });
                  };

                  return (
                    <div
                      key={day}
                      style={{
                        marginBottom: "16px",
                        padding: "16px",
                        background: "#f9fafb",
                        borderRadius: "8px",
                        border: "1px solid #e5e7eb",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          marginBottom: isClosed ? "0" : "12px",
                        }}
                      >
                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            cursor: "pointer",
                            minWidth: "150px",
                            fontWeight: 500,
                            fontSize: "14px",
                            color: "#374151",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={isClosed}
                            onChange={(e) => {
                              const newValue = e.target.checked
                                ? "Fechado"
                                : "08:00 - 18:00";
                              setFormData((prev) => {
                                if (!prev) return null;
                                return {
                                  ...prev,
                                  hours: {
                                    ...prev.hours,
                                    [day]: newValue,
                                  },
                                };
                              });
                            }}
                            style={{
                              width: "18px",
                              height: "18px",
                              cursor: "pointer",
                            }}
                          />
                          <span>{label}</span>
                        </label>
                        <span
                          style={{
                            marginLeft: "auto",
                            fontSize: "13px",
                            color: "#6b7280",
                            fontWeight: 500,
                          }}
                        >
                          {isClosed ? "Fechado" : ""}
                        </span>
                      </div>

                      {!isClosed && (
                        <div style={{ marginLeft: "26px" }}>
                          {hoursArray.map((hourRange, index) => {
                            const [openTime, closeTime] = hourRange.includes(
                              "-"
                            )
                              ? hourRange.split("-").map((t) => t.trim())
                              : ["08:00", "18:00"];

                            return (
                              <div
                                key={index}
                                style={{
                                  display: "flex",
                                  gap: "12px",
                                  alignItems: "center",
                                  marginBottom:
                                    index < hoursArray.length - 1
                                      ? "12px"
                                      : "0",
                                }}
                              >
                                <div style={{ flex: 1 }}>
                                  <label
                                    style={{
                                      display: "block",
                                      fontSize: "12px",
                                      color: "#6b7280",
                                      marginBottom: "4px",
                                    }}
                                  >
                                    Abre às(s)
                                  </label>
                                  <TimePickerCustom
                                    value={openTime}
                                    onChange={(value) =>
                                      updateTimeSlot(index, value, closeTime)
                                    }
                                  />
                                </div>

                                <div style={{ flex: 1 }}>
                                  <label
                                    style={{
                                      display: "block",
                                      fontSize: "12px",
                                      color: "#6b7280",
                                      marginBottom: "4px",
                                    }}
                                  >
                                    Fecha às(s)
                                  </label>
                                  <TimePickerCustom
                                    value={closeTime}
                                    onChange={(value) =>
                                      updateTimeSlot(index, openTime, value)
                                    }
                                  />
                                </div>

                                {hoursArray.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() => removeTimeSlot(index)}
                                    style={{
                                      marginTop: "20px",
                                      width: "36px",
                                      height: "36px",
                                      borderRadius: "8px",
                                      border: "1px solid #fee2e2",
                                      background: "#ffffff",
                                      cursor: "pointer",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      transition: "all 0.2s",
                                      fontSize: "18px",
                                      color: "#ef4444",
                                    }}
                                    onMouseOver={(e) => {
                                      e.currentTarget.style.background =
                                        "#fef2f2";
                                      e.currentTarget.style.borderColor =
                                        "#ef4444";
                                    }}
                                    onMouseOut={(e) => {
                                      e.currentTarget.style.background =
                                        "#ffffff";
                                      e.currentTarget.style.borderColor =
                                        "#fee2e2";
                                    }}
                                    title="Remover horário"
                                  >
                                    <span
                                      className="material-icons"
                                      style={{ fontSize: "18px" }}
                                    >
                                      delete
                                    </span>
                                  </button>
                                )}

                                {index === hoursArray.length - 1 && (
                                  <button
                                    type="button"
                                    onClick={addTimeSlot}
                                    style={{
                                      marginTop: "20px",
                                      width: "36px",
                                      height: "36px",
                                      borderRadius: "8px",
                                      border: "1px solid #e5e7eb",
                                      background: "#ffffff",
                                      cursor: "pointer",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      transition: "all 0.2s",
                                      fontSize: "20px",
                                      color: "#4285f4",
                                      fontWeight: 600,
                                    }}
                                    onMouseOver={(e) => {
                                      e.currentTarget.style.background =
                                        "#f0f9ff";
                                      e.currentTarget.style.borderColor =
                                        "#4285f4";
                                    }}
                                    onMouseOut={(e) => {
                                      e.currentTarget.style.background =
                                        "#ffffff";
                                      e.currentTarget.style.borderColor =
                                        "#e5e7eb";
                                    }}
                                    title="Adicionar horário adicional"
                                  >
                                    +
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}

                <div
                  style={{
                    marginTop: "16px",
                    padding: "16px",
                    background: "#f0f9ff",
                    border: "1px solid #bae6fd",
                    borderRadius: "8px",
                    display: "flex",
                    gap: "12px",
                    alignItems: "flex-start",
                  }}
                >
                  <span
                    className="material-icons"
                    style={{ color: "#0284c7", fontSize: "20px" }}
                  >
                    info
                  </span>
                  <div style={{ flex: 1 }}>
                    <strong
                      style={{
                        color: "#075985",
                        display: "block",
                        marginBottom: "4px",
                      }}
                    >
                      Dica:
                    </strong>
                    <span
                      style={{
                        color: "#0c4a6e",
                        fontSize: "14px",
                        lineHeight: "1.5",
                      }}
                    >
                      Marque "Fechado" para dias que não abre. Use o botão "+"
                      para adicionar múltiplos horários de funcionamento no
                      mesmo dia (como almoço: 11:00-14:00 e jantar:
                      18:00-22:00).
                    </span>
                  </div>
                </div>

                {/* Horários Especiais */}
                <SectionTitle style={{ marginTop: "32px" }}>
                  Horários especiais
                  <FieldSyncBadge kind="backend" />
                  <Tooltip
                    title="Confirme as datas comemorativas ou adicione o horário de funcionamento para que os clientes saibam quando sua empresa está aberta"
                    arrow
                    placement="top"
                  >
                    <InfoIcon className="material-icons">info</InfoIcon>
                  </Tooltip>
                </SectionTitle>

                <div
                  style={{
                    marginBottom: "16px",
                    padding: "12px 16px",
                    background: "#fffbeb",
                    border: "1px solid #fde68a",
                    borderRadius: "8px",
                    fontSize: "13px",
                    color: "#92400e",
                    lineHeight: "1.5",
                  }}
                >
                  Confirme as datas comemorativas ou adicione o horário de
                  funcionamento para que os clientes saibam quando sua empresa
                  está aberta.
                  <a
                    href="#"
                    style={{
                      color: "#4285f4",
                      textDecoration: "none",
                      fontWeight: 500,
                      marginLeft: "4px",
                    }}
                  >
                    Saiba mais
                  </a>
                </div>

                {(formData.specialHours || []).map((specialHour, index) => {
                  const [openTime, closeTime] = specialHour.hours?.includes("-")
                    ? specialHour.hours.split("-").map((t) => t.trim())
                    : ["", ""];

                  return (
                    <div
                      key={index}
                      style={{
                        marginBottom: "16px",
                        padding: "16px",
                        background: "#ffffff",
                        borderRadius: "8px",
                        border: "1px solid #e5e7eb",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          marginBottom: "12px",
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div
                            style={{
                              fontSize: "14px",
                              fontWeight: 600,
                              color: "#111827",
                              marginBottom: "4px",
                            }}
                          >
                            {specialHour.date
                              ? new Date(specialHour.date).toLocaleDateString(
                                  "pt-BR",
                                  {
                                    day: "2-digit",
                                    month: "short",
                                    year: "numeric",
                                  }
                                )
                              : "Nova data"}
                          </div>
                          <Input
                            type="date"
                            value={specialHour.date}
                            onChange={(e) => {
                              setFormData((prev) => {
                                if (!prev) return null;
                                const updated = [...(prev.specialHours || [])];
                                updated[index] = {
                                  ...updated[index],
                                  date: e.target.value,
                                };
                                return { ...prev, specialHours: updated };
                              });
                            }}
                            style={{
                              marginTop: "8px",
                              padding: "8px 12px",
                              fontSize: "14px",
                            }}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setFormData((prev) => {
                              if (!prev) return null;
                              const updated = (prev.specialHours || []).filter(
                                (_, i) => i !== index
                              );
                              return { ...prev, specialHours: updated };
                            });
                          }}
                          style={{
                            marginLeft: "12px",
                            padding: "8px",
                            background: "transparent",
                            border: "none",
                            cursor: "pointer",
                            color: "#6b7280",
                            fontSize: "14px",
                            fontWeight: 500,
                            transition: "color 0.2s",
                          }}
                          onMouseOver={(e) =>
                            (e.currentTarget.style.color = "#4285f4")
                          }
                          onMouseOut={(e) =>
                            (e.currentTarget.style.color = "#6b7280")
                          }
                        >
                          Editar
                        </button>
                      </div>

                      <div
                        style={{
                          display: "flex",
                          gap: "12px",
                          alignItems: "center",
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <label
                            style={{
                              display: "block",
                              fontSize: "12px",
                              color: "#6b7280",
                              marginBottom: "4px",
                            }}
                          >
                            Abre às(s)
                          </label>
                          <TimePickerCustom
                            value={openTime}
                            onChange={(value) => {
                              const newHours = `${value} - ${closeTime}`;
                              setFormData((prev) => {
                                if (!prev) return null;
                                const updated = [...(prev.specialHours || [])];
                                updated[index] = {
                                  ...updated[index],
                                  hours: newHours,
                                };
                                return { ...prev, specialHours: updated };
                              });
                            }}
                          />
                        </div>

                        <div style={{ flex: 1 }}>
                          <label
                            style={{
                              display: "block",
                              fontSize: "12px",
                              color: "#6b7280",
                              marginBottom: "4px",
                            }}
                          >
                            Fecha às(s)
                          </label>
                          <TimePickerCustom
                            value={closeTime}
                            onChange={(value) => {
                              const newHours = `${openTime} - ${value}`;
                              setFormData((prev) => {
                                if (!prev) return null;
                                const updated = [...(prev.specialHours || [])];
                                updated[index] = {
                                  ...updated[index],
                                  hours: newHours,
                                };
                                return { ...prev, specialHours: updated };
                              });
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}

                <button
                  type="button"
                  onClick={() => {
                    setFormData((prev) => {
                      if (!prev) return null;
                      const newSpecialHour = {
                        date: "",
                        hours: "08:00 - 18:00",
                      };
                      return {
                        ...prev,
                        specialHours: [
                          ...(prev.specialHours || []),
                          newSpecialHour,
                        ],
                      };
                    });
                  }}
                  style={{
                    width: "100%",
                    padding: "12px",
                    background: "#ffffff",
                    border: "2px dashed #d1d5db",
                    borderRadius: "8px",
                    color: "#4285f4",
                    fontSize: "14px",
                    fontWeight: 500,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                    transition: "all 0.2s",
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = "#f0f9ff";
                    e.currentTarget.style.borderColor = "#4285f4";
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = "#ffffff";
                    e.currentTarget.style.borderColor = "#d1d5db";
                  }}
                >
                  <span className="material-icons" style={{ fontSize: "20px" }}>
                    add
                  </span>
                  Adicionar data
                </button>
              </FormSection>
            )}

            {activeTab === "descricao" && (
              <FormSection>
                <SectionTitle>
                  Descrição do Negócio
                  <FieldSyncBadge kind="backend" />
                </SectionTitle>

                <FormGroup>
                  <Label htmlFor="description">
                    <span className="material-icons">description</span>
                    Descrição Detalhada
                    <FieldSyncBadge kind="backend" />
                    <Tooltip
                      title="Escreva uma descrição envolvente sobre seu negócio. Destaque seus diferenciais, história e o que torna você único. Isso ajuda no SEO e atrai mais clientes"
                      arrow
                      placement="top"
                    >
                      <InfoIcon className="material-icons">info</InfoIcon>
                    </Tooltip>
                  </Label>
                  <TextArea
                    id="description"
                    name="description"
                    value={formData.description || ""}
                    onChange={handleChange}
                    placeholder="Conte aos clientes sobre seu negócio, os serviços/produtos que oferece, seus diferenciais e o que torna sua empresa especial..."
                    maxLength={2000}
                  />
                  <CharCounter
                    $overLimit={(formData.description?.length || 0) > 2000}
                  >
                    {formData.description?.length || 0}/2000 caracteres
                  </CharCounter>
                </FormGroup>

                <div
                  style={{
                    marginTop: "16px",
                    padding: "16px",
                    background: "#f0f9ff",
                    border: "1px solid #bae6fd",
                    borderRadius: "8px",
                    display: "flex",
                    gap: "12px",
                    alignItems: "flex-start",
                  }}
                >
                  <span
                    className="material-icons"
                    style={{ color: "#0284c7", fontSize: "20px" }}
                  >
                    info
                  </span>
                  <div style={{ flex: 1 }}>
                    <strong
                      style={{
                        color: "#075985",
                        display: "block",
                        marginBottom: "4px",
                      }}
                    >
                      Dica:
                    </strong>
                    <span
                      style={{
                        color: "#0c4a6e",
                        fontSize: "14px",
                        lineHeight: "1.5",
                      }}
                    >
                      Uma boa descrição com detalhes sobre sua empresa,
                      histórico, especialidades e diferenciais ajuda a atrair
                      mais clientes e melhora seu posicionamento nos resultados
                      de busca.
                    </span>
                  </div>
                </div>
              </FormSection>
            )}

            {activeTab === "servicos" && (
              <ServicesTab profile={formData} />
            )}



            {activeTab === "postagens" && (
              <PostsTab profile={formData} />
            )}

            {activeTab === "gabarito" && (
              <GabaritoTab profile={formData} />
            )}

            {activeTab === "presenca" && (
              <PresencaDigitalTab profile={profile ?? formData} />
            )}

            {activeTab === "seo" && (
              <SeoTab profile={formData} />
            )}

            {activeTab === "atributos" && (
              <FormSection>
                <SectionTitle>
                  Acessibilidade
                  <FieldSyncBadge kind="backend" />
                </SectionTitle>

                <FormGroup>
                  <Label>
                    <span className="material-icons">accessible</span>
                    Recursos de Acessibilidade
                    <FieldSyncBadge kind="backend" />
                    <Tooltip
                      title="Indique os recursos de acessibilidade disponíveis no seu estabelecimento para pessoas com deficiência ou mobilidade reduzida"
                      arrow
                      placement="top"
                    >
                      <InfoIcon className="material-icons">info</InfoIcon>
                    </Tooltip>
                  </Label>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                    }}
                  >
                    {[
                      "Entrada com acessibilidade",
                      "Estacionamento acessível",
                      "Banheiro acessível",
                      "Elevador acessível",
                    ].map((item) => (
                      <label
                        key={item}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          cursor: "pointer",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={
                            formData.accessibility?.includes(item) || false
                          }
                          onChange={(e) => {
                            const current = formData.accessibility || [];
                            const updated = e.target.checked
                              ? [...current, item]
                              : current.filter((i) => i !== item);
                            setFormData((prev) =>
                              prev ? { ...prev, accessibility: updated } : null
                            );
                          }}
                          style={{ width: "18px", height: "18px" }}
                        />
                        <span style={{ fontSize: "14px" }}>{item}</span>
                      </label>
                    ))}
                  </div>
                </FormGroup>

                <SectionTitle style={{ marginTop: "24px" }}>
                  Estacionamento
                  <FieldSyncBadge kind="backend" />
                </SectionTitle>

                <FormGroup>
                  <Label>
                    <span className="material-icons">local_parking</span>
                    Opções de Estacionamento
                    <FieldSyncBadge kind="backend" />
                    <Tooltip
                      title="Informe as opções de estacionamento disponíveis para seus clientes. Isso ajuda na decisão de visita presencial"
                      arrow
                      placement="top"
                    >
                      <InfoIcon className="material-icons">info</InfoIcon>
                    </Tooltip>
                  </Label>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                    }}
                  >
                    {[
                      "Estacionamento gratuito",
                      "Estacionamento pago",
                      "Estacionamento na rua",
                      "Valet",
                    ].map((item) => (
                      <label
                        key={item}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          cursor: "pointer",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={formData.parking?.includes(item) || false}
                          onChange={(e) => {
                            const current = formData.parking || [];
                            const updated = e.target.checked
                              ? [...current, item]
                              : current.filter((i) => i !== item);
                            setFormData((prev) =>
                              prev ? { ...prev, parking: updated } : null
                            );
                          }}
                          style={{ width: "18px", height: "18px" }}
                        />
                        <span style={{ fontSize: "14px" }}>{item}</span>
                      </label>
                    ))}
                  </div>
                </FormGroup>

                <SectionTitle style={{ marginTop: "24px" }}>
                  Formas de Pagamento
                  <FieldSyncBadge kind="backend" />
                </SectionTitle>

                <FormGroup>
                  <Label>
                    <span className="material-icons">payment</span>
                    Métodos Aceitos
                    <FieldSyncBadge kind="backend" />
                    <Tooltip
                      title="Selecione todas as formas de pagamento aceitas no seu negócio. Clientes frequentemente filtram por método de pagamento preferido"
                      arrow
                      placement="top"
                    >
                      <InfoIcon className="material-icons">info</InfoIcon>
                    </Tooltip>
                  </Label>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                    }}
                  >
                    {[
                      "Cartões de crédito",
                      "Cartões de débito",
                      "Dinheiro",
                      "Pix",
                      "Pagamento por NFC",
                      "Visa",
                      "Mastercard",
                      "American Express",
                    ].map((item) => (
                      <label
                        key={item}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          cursor: "pointer",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={formData.payments?.includes(item) || false}
                          onChange={(e) => {
                            const current = formData.payments || [];
                            const updated = e.target.checked
                              ? [...current, item]
                              : current.filter((i) => i !== item);
                            setFormData((prev) =>
                              prev ? { ...prev, payments: updated } : null
                            );
                          }}
                          style={{ width: "18px", height: "18px" }}
                        />
                        <span style={{ fontSize: "14px" }}>{item}</span>
                      </label>
                    ))}
                  </div>
                </FormGroup>

                <SectionTitle style={{ marginTop: "24px" }}>
                  Opções de Serviço
                  <FieldSyncBadge kind="backend" />
                </SectionTitle>

                <FormGroup>
                  <Label>
                    <span className="material-icons">room_service</span>
                    Como você atende seus clientes
                    <FieldSyncBadge kind="backend" />
                    <Tooltip
                      title="Especifique as modalidades de atendimento oferecidas. Ex: Delivery, retirada, presencial. Isso ajuda clientes a encontrarem o serviço que precisam"
                      arrow
                      placement="top"
                    >
                      <InfoIcon className="material-icons">info</InfoIcon>
                    </Tooltip>
                  </Label>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                    }}
                  >
                    {[
                      "Atendimento presencial",
                      "Entrega",
                      "Retirada na loja",
                      "Serviço online",
                      "Drive-through",
                    ].map((item) => (
                      <label
                        key={item}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          cursor: "pointer",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={
                            formData.serviceOptions?.includes(item) || false
                          }
                          onChange={(e) => {
                            const current = formData.serviceOptions || [];
                            const updated = e.target.checked
                              ? [...current, item]
                              : current.filter((i) => i !== item);
                            setFormData((prev) =>
                              prev ? { ...prev, serviceOptions: updated } : null
                            );
                          }}
                          style={{ width: "18px", height: "18px" }}
                        />
                        <span style={{ fontSize: "14px" }}>{item}</span>
                      </label>
                    ))}
                  </div>
                </FormGroup>

                <SectionTitle style={{ marginTop: "24px" }}>
                  Comodidades
                  <FieldSyncBadge kind="backend" />
                </SectionTitle>

                <FormGroup>
                  <Label>
                    <span className="material-icons">star</span>
                    Recursos Adicionais
                    <FieldSyncBadge kind="backend" />
                    <Tooltip
                      title="Destaque recursos extras que tornam seu negócio mais atrativo. Ex: Wi-Fi, ar-condicionado, aceita pets, etc."
                      arrow
                      placement="top"
                    >
                      <InfoIcon className="material-icons">info</InfoIcon>
                    </Tooltip>
                  </Label>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                    }}
                  >
                    {[
                      "Wi-Fi gratuito",
                      "Ar-condicionado",
                      "Pet-friendly",
                      "Área externa",
                      "Brinquedoteca",
                    ].map((item) => (
                      <label
                        key={item}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          cursor: "pointer",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={formData.amenities?.includes(item) || false}
                          onChange={(e) => {
                            const current = formData.amenities || [];
                            const updated = e.target.checked
                              ? [...current, item]
                              : current.filter((i) => i !== item);
                            setFormData((prev) =>
                              prev ? { ...prev, amenities: updated } : null
                            );
                          }}
                          style={{ width: "18px", height: "18px" }}
                        />
                        <span style={{ fontSize: "14px" }}>{item}</span>
                      </label>
                    ))}
                  </div>
                </FormGroup>
              </FormSection>
            )}

            {activeTab === "ia" && (
              <FormSection>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    marginBottom: "24px",
                    padding: "16px",
                    background: "#f0f9ff",
                    borderRadius: "12px",
                    border: "1px solid #bae6fd",
                  }}
                >
                  <span
                    className="material-icons"
                    style={{ fontSize: "32px", color: "#4285f4" }}
                  >
                    auto_awesome
                  </span>
                  <div>
                    <h3
                      style={{
                        margin: 0,
                        fontSize: "18px",
                        fontWeight: 600,
                        color: "#1f2937",
                      }}
                    >
                      Assistente IA
                      <FieldSyncBadge kind="ia" />
                    </h3>
                  </div>
                </div>

                <FormGroup>
                  <Label htmlFor="ia-description">
                    <span className="material-icons">description</span>
                    Descreva seu negócio ou o que deseja mudar
                    <FieldSyncBadge kind="ia" />
                  </Label>
                  <TextArea
                    id="ia-description"
                    name="ia-description"
                    value={iaDescription}
                    onChange={(e) => setIaDescription(e.target.value)}
                    placeholder="Ex: Restaurante italiano familiar com pratos tradicionais, localização central..."
                    style={{ minHeight: "120px" }}
                  />
                </FormGroup>

                <div
                  style={{
                    marginTop: "12px",
                    padding: "12px 14px",
                    background: "#f8fafc",
                    border: "1px solid #e5e7eb",
                    borderRadius: "10px",
                    color: "#4b5563",
                    fontSize: "13px",
                    lineHeight: "1.5",
                  }}
                >
                  A IA usa o nome, local, categoria atual, contexto escrito aqui
                  e palavras do Gabarito para sugerir preenchimentos de SEO.
                </div>

                {iaFeedback && (
                  <div
                    style={{
                      marginTop: "12px",
                      padding: "12px 14px",
                      background:
                        iaFeedback.type === "error"
                          ? "#fef2f2"
                          : iaFeedback.type === "success"
                          ? "#ecfdf5"
                          : "#eff6ff",
                      border:
                        iaFeedback.type === "error"
                          ? "1px solid #fecaca"
                          : iaFeedback.type === "success"
                          ? "1px solid #a7f3d0"
                          : "1px solid #bfdbfe",
                      borderRadius: "10px",
                      color:
                        iaFeedback.type === "error"
                          ? "#991b1b"
                          : iaFeedback.type === "success"
                          ? "#065f46"
                          : "#1e40af",
                      fontSize: "14px",
                      fontWeight: 600,
                    }}
                  >
                    {iaFeedback.message}
                  </div>
                )}

                <div
                  style={{
                    display: "flex",
                    gap: "12px",
                    marginTop: "16px",
                    flexWrap: "wrap",
                  }}
                >
                  <Button
                    type="button"
                    onClick={handleGenerateCommercialPlan}
                    disabled={iaLoadingAction !== null}
                    style={{
                      background: "#047857",
                      border: "none",
                      padding: "10px 20px",
                      borderRadius: "8px",
                      color: "white",
                      cursor: iaLoadingAction !== null ? "not-allowed" : "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      fontSize: "14px",
                      fontWeight: 600,
                      transition: "all 0.2s",
                      opacity: iaLoadingAction !== null ? 0.6 : 1,
                    }}
                    onMouseOver={(e) => {
                      if (iaLoadingAction === null)
                        e.currentTarget.style.background = "#065f46";
                    }}
                    onMouseOut={(e) => {
                      if (iaLoadingAction === null)
                        e.currentTarget.style.background = "#047857";
                    }}
                  >
                    <span className="material-icons" style={{ fontSize: "18px" }}>
                      campaign
                    </span>
                    {iaLoadingAction === "commercial"
                      ? "Mapeando..."
                      : "Gerar plano comercial para SEO"}
                  </Button>

                  <Button
                    type="button"
                    onClick={handleSuggestKeywordsWithContext}
                    disabled={iaLoadingAction !== null}
                    style={{
                      background: "#4285f4",
                      border: "none",
                      padding: "10px 20px",
                      borderRadius: "8px",
                      color: "white",
                      cursor: iaLoadingAction !== null ? "not-allowed" : "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      fontSize: "14px",
                      fontWeight: 500,
                      transition: "all 0.2s",
                      opacity: iaLoadingAction !== null ? 0.6 : 1,
                    }}
                    onMouseOver={(e) => {
                      if (iaLoadingAction === null)
                        e.currentTarget.style.background = "#1a73e8";
                    }}
                    onMouseOut={(e) => {
                      if (iaLoadingAction === null)
                        e.currentTarget.style.background = "#4285f4";
                    }}
                  >
                    <span
                      className="material-icons"
                      style={{ fontSize: "18px" }}
                    >
                      refresh
                    </span>
                    {iaLoadingAction === "keywords"
                      ? "Processando..."
                      : "Sugerir palavras-chave"}
                  </Button>

                  <Button
                    type="button"
                    onClick={handleSuggestCategoriesWithContext}
                    disabled={iaLoadingAction !== null}
                    style={{
                      background: "#4285f4",
                      border: "none",
                      padding: "10px 20px",
                      borderRadius: "8px",
                      color: "white",
                      cursor: iaLoadingAction !== null ? "not-allowed" : "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      fontSize: "14px",
                      fontWeight: 500,
                      transition: "all 0.2s",
                      opacity: iaLoadingAction !== null ? 0.6 : 1,
                    }}
                    onMouseOver={(e) => {
                      if (iaLoadingAction === null)
                        e.currentTarget.style.background = "#1a73e8";
                    }}
                    onMouseOut={(e) => {
                      if (iaLoadingAction === null)
                        e.currentTarget.style.background = "#4285f4";
                    }}
                  >
                    <span
                      className="material-icons"
                      style={{ fontSize: "18px" }}
                    >
                      category
                    </span>
                    {iaLoadingAction === "categories"
                      ? "Processando..."
                      : "Sugerir categorias"}
                  </Button>

                  <Button
                    type="button"
                    onClick={handleGenerateBriefingWithContext}
                    disabled={iaLoadingAction !== null}
                    style={{
                      background: "#4285f4",
                      border: "none",
                      padding: "10px 20px",
                      borderRadius: "8px",
                      color: "white",
                      cursor: iaLoadingAction !== null ? "not-allowed" : "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      fontSize: "14px",
                      fontWeight: 500,
                      transition: "all 0.2s",
                      opacity: iaLoadingAction !== null ? 0.6 : 1,
                    }}
                    onMouseOver={(e) => {
                      if (iaLoadingAction === null)
                        e.currentTarget.style.background = "#1a73e8";
                    }}
                    onMouseOut={(e) => {
                      if (iaLoadingAction === null)
                        e.currentTarget.style.background = "#4285f4";
                    }}
                  >
                    <span
                      className="material-icons"
                      style={{ fontSize: "18px" }}
                    >
                      tune
                    </span>
                    {iaLoadingAction === "briefing"
                      ? "Processando..."
                      : "Gerar descrição"}
                  </Button>
                </div>

                {commercialPlan && (
                  <div
                    style={{
                      marginTop: "16px",
                      padding: "16px",
                      background: "#ecfdf5",
                      border: "1px solid #a7f3d0",
                      borderRadius: "10px",
                    }}
                  >
                    <strong style={{ color: "#065f46" }}>
                      Plano comercial pronto
                    </strong>
                    <p
                      style={{
                        color: "#475569",
                        fontSize: "13px",
                        lineHeight: 1.5,
                        margin: "8px 0 12px",
                      }}
                    >
                      {
                        commercialPlan.itens.filter(
                          (item) => item.uso === "servico",
                        ).length
                      }{" "}
                      serviços,{" "}
                      {commercialPlan.produtos.length +
                        commercialPlan.itens.filter(
                          (item) => item.uso === "produto",
                        ).length}{" "}
                      produtos e {keywordDrafts.length} palavras com intenção
                      comercial. A aba SEO usa exatamente este plano.
                    </p>
                    <Button
                      type="button"
                      onClick={() => setActiveTab("seo")}
                      style={{
                        background: "#047857",
                        border: "none",
                        borderRadius: "8px",
                        color: "#fff",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "8px",
                        padding: "9px 16px",
                        cursor: "pointer",
                      }}
                    >
                      <span className="material-icons" style={{ fontSize: "18px" }}>
                        travel_explore
                      </span>
                      Revisar e publicar na aba SEO
                    </Button>
                  </div>
                )}

                {keywordDrafts.length > 0 && (
                  <div
                    style={{
                      marginTop: "16px",
                      padding: "16px",
                      background: "#f0f9ff",
                      border: "1px solid #bae6fd",
                      borderRadius: "8px",
                    }}
                  >
                    <strong
                      style={{
                        display: "block",
                        marginBottom: "12px",
                        color: "#0c4a6e",
                      }}
                    >
                      Palavras-chave Sugeridas:
                    </strong>
                    <span
                      style={{
                        display: "block",
                        color: "#475569",
                        fontSize: "13px",
                        marginBottom: "12px",
                      }}
                    >
                      Revise, corrija ou desmarque antes de aplicar. Nada vai
                      para o Gabarito sem essa confirmação.
                    </span>
                    <div style={{ display: "grid", gap: "10px" }}>
                      {keywordDrafts.map((keyword) => (
                        <div
                          key={keyword.id}
                          style={{
                            alignItems: "center",
                            background: "#ffffff",
                            border: "1px solid #dbeafe",
                            borderRadius: "10px",
                            display: "grid",
                            gap: "10px",
                            gridTemplateColumns: "24px 1fr 32px",
                            padding: "8px",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={keyword.selected}
                            onChange={(event) =>
                              setKeywordDrafts((prev) =>
                                updateKeywordDraftSelection(
                                  prev,
                                  keyword.id,
                                  event.target.checked,
                                ),
                              )
                            }
                            style={{
                              accentColor: "#1a73e8",
                              height: "18px",
                              width: "18px",
                            }}
                          />
                          <Input
                            value={keyword.value}
                            onChange={(event) =>
                              setKeywordDrafts((prev) =>
                                updateKeywordDraftValue(
                                  prev,
                                  keyword.id,
                                  event.target.value,
                                ),
                              )
                            }
                            style={{ padding: "9px 10px" }}
                          />
                          <button
                            type="button"
                            onClick={() =>
                              setKeywordDrafts((prev) =>
                                removeKeywordDraft(prev, keyword.id),
                              )
                            }
                            style={{
                              alignItems: "center",
                              background: "transparent",
                              border: "none",
                              color: "#64748b",
                              cursor: "pointer",
                              display: "inline-flex",
                              justifyContent: "center",
                              padding: "4px",
                            }}
                          >
                            <span
                              className="material-icons"
                              style={{ fontSize: "18px" }}
                            >
                              close
                            </span>
                          </button>
                        </div>
                      ))}
                    </div>
                    <Button
                      type="button"
                      onClick={handleApplyKeywordsToGabarito}
                      disabled={selectedKeywordValues.length === 0}
                      style={{
                        marginTop: "14px",
                        background: "#1a73e8",
                        border: "none",
                        padding: "9px 16px",
                        borderRadius: "8px",
                        color: "white",
                        cursor:
                          selectedKeywordValues.length === 0
                            ? "not-allowed"
                            : "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "8px",
                        fontSize: "13px",
                        fontWeight: 600,
                        opacity: selectedKeywordValues.length === 0 ? 0.6 : 1,
                      }}
                    >
                      <span
                        className="material-icons"
                        style={{ fontSize: "17px" }}
                      >
                        assignment
                      </span>
                      Aplicar selecionadas no Gabarito
                    </Button>
                  </div>
                )}

                <div
                  style={{
                    marginTop: "24px",
                    padding: "16px",
                    background: "#fffbeb",
                    border: "1px solid #fcd34d",
                    borderRadius: "8px",
                    display: "flex",
                    gap: "12px",
                    alignItems: "flex-start",
                  }}
                >
                  <span
                    className="material-icons"
                    style={{ color: "#f59e0b", fontSize: "20px" }}
                  >
                    info
                  </span>
                  <div style={{ flex: 1 }}>
                    <strong
                      style={{
                        color: "#92400e",
                        display: "block",
                        marginBottom: "4px",
                      }}
                    >
                      Nota:
                    </strong>
                    <span
                      style={{
                        color: "#78350f",
                        fontSize: "14px",
                        lineHeight: "1.5",
                      }}
                    >
                      A IA não altera horários de funcionamento, produtos ou
                      serviços por segurança.
                    </span>
                  </div>
                </div>
              </FormSection>
            )}
          </ModalBody>

          <ModalFooter>
            <FooterInfo>
              <span className="material-icons">info</span>
              <span>
                Última atualização: {new Date().toLocaleDateString("pt-BR")}
              </span>
            </FooterInfo>
            {saveFeedback && (
              <SaveFeedback role="alert">
                <span className="material-icons">error</span>
                {saveFeedback}
              </SaveFeedback>
            )}
            <ButtonGroup>
              <Button type="button" $secondary onClick={onClose}>
                <span className="material-icons">close</span>
                <span>Cancelar</span>
              </Button>
              <Button type="submit" disabled={loading}>
                <span className="material-icons">save</span>
                <span>{loading ? "Salvando..." : "Salvar Alterações"}</span>
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </form>
      </ModalContainer>
    </ModalOverlay>
  );
};
